import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Clock, FileText, Image, CheckCircle, XCircle, ToggleLeft, ToggleRight, Wallet, TrendingUp, ArrowDownCircle, Plus, AlertCircle, Star, Edit2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import { useFetch } from '../hooks';
import { doctorService } from '../services/api';
import { getStatusColor, formatDate, formatCurrency } from '../utils/helpers';
import { getCurrencyConfig } from '../utils/currencyConfig';

const TABS = ['All', 'Today', 'Upcoming', 'Past', 'Cancelled'];

const today = new Date().toISOString().split('T')[0];

/* ── Withdraw Status Badge ──────────────────────────────────────────────────── */
const WithdrawStatusBadge = ({ status }) => {
  const map = {
    pending:  { cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', icon: AlertCircle },
    approved: { cls: 'bg-green-100  text-green-800  dark:bg-green-900/40  dark:text-green-300',  icon: CheckCircle },
    rejected: { cls: 'bg-red-100    text-red-800    dark:bg-red-900/40    dark:text-red-300',    icon: XCircle     },
  };
  const { cls, icon: Icon } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cls}`}>
      <Icon className="w-3 h-3" />{status}
    </span>
  );
};

const filterAppointments = (appointments, tab) => {
  if (!appointments) return [];
  switch (tab) {
    case 'Today':    return appointments.filter(a => a.appointment_date?.split('T')[0] === today);
    case 'Upcoming': return appointments.filter(a => a.appointment_date?.split('T')[0] > today && a.status !== 'cancelled');
    case 'Past':     return appointments.filter(a => a.appointment_date?.split('T')[0] < today && a.status !== 'cancelled');
    case 'Cancelled':return appointments.filter(a => a.status === 'cancelled');
    default:         return appointments;
  }
};

/* ── Edit Doctor Form ───────────────────────────────────────────────────── */
const EditDoctorForm = ({ doctor, onSubmit, onClose }) => {
  const { values, handleChange, handleSubmit, setValues } = useForm({
    name:               doctor.name || '',
    email:              doctor.email || '',
    mobile:             doctor.mobile || '',
    specialization:     doctor.specialization || '',
    qualifications:     doctor.qualifications || '',
    experience_years:   doctor.experience_years || '',
    consultation_fee:   doctor.consultation_fee || '',
    consultancy_type:   doctor.consultancy_type || 'online',
    address:            doctor.address || '',
    city:               doctor.city || '',
    commission_percent: doctor.commission_percent || '',
    status:             doctor.status || 'active',
    registration_number:   doctor.registration_number || '',
    medical_council_name:  doctor.medical_council_name || '',
    clinic_name:           doctor.clinic_name || '',
    clinic_phone:          doctor.clinic_phone || '',
    bio:                   doctor.bio || '',
    profile_img:           null,
    registration_certificate_url: null,
    degree_certificate_url:       null,
    id_proof_url:                 null,
  }, { name: { required: true }, email: { required: true, email: true } });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Full Name" required>
          <input name="name" value={values.name} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Email" required>
          <input name="email" type="email" value={values.email} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Mobile">
          <input name="mobile" value={values.mobile} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Specialization">
          <input name="specialization" value={values.specialization} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Qualifications">
          <input name="qualifications" value={values.qualifications} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Experience (years)">
          <input name="experience_years" type="number" value={values.experience_years} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label={`Consultation Fee (${getCurrencyConfig().symbol})`}>
          <input name="consultation_fee" type="number" value={values.consultation_fee} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Consultancy Type">
          <select name="consultancy_type" value={values.consultancy_type} onChange={handleChange} className="input-field">
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="both">Both</option>
          </select>
        </FormField>
        <FormField label="Commission (%)">
          <input name="commission_percent" type="number" min="0" max="100" value={values.commission_percent} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Status">
          <select name="status" value={values.status} onChange={handleChange} className="input-field">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
        <FormField label="Address">
          <input name="address" value={values.address} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="City / Area">
          <input name="city" value={values.city} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Registration Number">
          <input name="registration_number" value={values.registration_number} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Medical Council Name">
          <input name="medical_council_name" value={values.medical_council_name} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Clinic / Hospital Name">
          <input name="clinic_name" value={values.clinic_name} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Clinic Phone">
          <input name="clinic_phone" value={values.clinic_phone} onChange={handleChange} className="input-field" />
        </FormField>
      </div>
      <FormField label="Bio / Description">
        <textarea name="bio" value={values.bio} onChange={handleChange} className="input-field resize-none" rows={3} />
      </FormField>
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Documents (leave empty to keep current)</p>
        <div className="grid grid-cols-1 gap-3">
          {[
            { label: 'Profile Image',                    key: 'profile_img',                   accept: 'image/*' },
            { label: 'Medical Registration Certificate', key: 'registration_certificate_url',   accept: 'image/*,.pdf' },
            { label: 'Degree Certificate',               key: 'degree_certificate_url',         accept: 'image/*,.pdf' },
            { label: 'ID Proof (Aadhar / PAN)',          key: 'id_proof_url',                   accept: 'image/*,.pdf' },
          ].map(({ label, key, accept }) => (
            <FormField key={key} label={label}>
              {values[key] ? (
                <div className="flex items-center gap-2 input-field py-2">
                  {key === 'profile_img' ? (
                    <img src={URL.createObjectURL(values[key])} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                  ) : (
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{values[key].name}</span>
                  <button type="button" onClick={() => setValues(p => ({ ...p, [key]: null }))}
                    className="shrink-0 text-red-500 hover:text-red-700 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer input-field py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-medihub-primary transition-colors">
                  <Upload className="w-4 h-4 shrink-0 text-gray-400" />
                  <span>Upload {label}…</span>
                  <input type="file" accept={accept} className="hidden"
                    onChange={e => setValues(p => ({ ...p, [key]: e.target.files[0] || null }))} />
                </label>
              )}
            </FormField>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">Update Doctor</button>
      </div>
    </Form>
  );
};

const DoctorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [previewFile, setPreviewFile] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const { data: doctorData, loading: docLoading, refetch } = useFetch(() => doctorService.getById(id), [id]);

  const doctor       = doctorData?.profile || null;
  const appointments = doctorData?.appointments?.list || [];
  const apptSummary  = doctorData?.appointments?.summary || {};
  const earnings     = doctorData?.earnings?.list || [];
  const earnSummary  = doctorData?.earnings?.summary || {};
  const withdrawals  = doctorData?.withdrawals?.list || [];
  const wdSummary    = doctorData?.withdrawals?.summary || {};
  const prescriptions = doctorData?.prescriptions || [];

  const handleEditSave = async (values) => {
    try {
      const res = await doctorService.update(id, values);
      toast.success(res?.message || 'Doctor updated!');
      setEditModal(false);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed');
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = doctor.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await doctorService.toggleStatus(doctor.id, newStatus);
      toast.success(res?.message || 'Status updated');
      refetch();
    } catch { toast.error('Failed'); }
  };

  const handleToggleVerified = async () => {
    const newVerified = doctor.is_verified ? 0 : 1;
    try {
      const res = await doctorService.toggleVerified(doctor.id, newVerified);
      toast.success(res?.message || 'Verification updated');
      refetch();
    } catch { toast.error('Failed'); }
  };

  const handleRequestWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > doctor.walletBalance) { toast.error('Amount exceeds wallet balance'); return; }
    setWithdrawing(true);
    try {
      await doctorService.requestWithdraw(doctor.id, amt);
      toast.success('Withdraw request submitted');
      setWithdrawModal(false);
      setWithdrawAmount('');
      refetch();
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setWithdrawing(false); }
  };

  const handleApproveWithdraw = async (requestId) => {
    try {
      await doctorService.approveWithdraw(doctor.id, requestId);
      toast.success('Withdraw approved');
      refetch();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const handleRejectWithdraw = async (requestId) => {
    try {
      await doctorService.rejectWithdraw(doctor.id, requestId);
      toast.success('Withdraw rejected');
      refetch();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  if (docLoading) return (
    <div className="space-y-4">
      <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      <div className="card p-6 h-40 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />
    </div>
  );

  if (!doctor) return (
    <div className="text-center py-20">
      <p className="text-gray-500">Doctor not found.</p>
      <button onClick={() => navigate('/doctors')} className="btn-primary mt-4">Back to Doctors</button>
    </div>
  );

  const filtered = filterAppointments(appointments, activeTab);
  const stats = {
    total:     apptSummary.total     || 0,
    today:     appointments.filter(a => a.appointment_date?.split('T')[0] === today).length,
    upcoming:  apptSummary.upcoming  || 0,
    completed: apptSummary.completed || 0,
    cancelled: apptSummary.cancelled || 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/doctors")}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Doctor Profile
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Detailed view and appointment history
          </p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 bg-medihub-light dark:bg-medihub-dark/40 flex items-center justify-center">
            {doctor.profile_img ? (
              <img
                src={doctor.profile_img}
                alt={doctor.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-medihub-secondary dark:text-medihub-highlight font-bold text-3xl">
                {doctor.name[0]}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {doctor.name}
                </h2>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {doctor.specialization && (
                    <span className="badge bg-medihub-light text-medihub-secondary dark:bg-medihub-dark/40 dark:text-medihub-highlight">
                      {doctor.specialization}
                    </span>
                  )}
                  {doctor.qualifications && (
                    <span className="badge bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {doctor.qualifications}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditModal(true)}
                  className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 bg-white dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                {/* Status Toggle */}
                <button
                  onClick={handleToggleStatus}
                  className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${doctor.status === "active" ? "border-green-300 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700" : "border-gray-300 text-gray-500 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"}`}
                >
                  {doctor.status === "active" ? (
                    <ToggleRight className="w-4 h-4" />
                  ) : (
                    <ToggleLeft className="w-4 h-4" />
                  )}
                  {doctor.status === "active" ? "Active" : "Inactive"}
                </button>
                {/* Verified Toggle */}
                <button
                  onClick={handleToggleVerified}
                  className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${doctor.verified ? "border-medihub-primary text-medihub-secondary bg-medihub-light dark:bg-medihub-dark/20 dark:text-medihub-highlight dark:border-medihub-accent" : "border-gray-300 text-gray-500 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"}`}
                >
                  {doctor.is_verified ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {doctor.is_verified ? "Verified" : "Unverified"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-4">
              {[
                { icon: Mail,  label: doctor.email },
                { icon: Phone, label: doctor.mobile },
                { icon: Star,  label: `${doctor.rating || '—'} Rating` },
                { icon: Clock, label: `${doctor.experience_years} yrs exp` },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Icon className="w-4 h-4 shrink-0 text-gray-400" />{label}
                </div>
              ))}
            </div>

            {/* Fees + Commission */}
            <div className="mt-4 flex flex-wrap gap-4">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-2">
                <p className="text-xs text-gray-500">Consultation Fee</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">{doctor.consultation_fee ? formatCurrency(doctor.consultation_fee) : '—'}</p>
              </div>
              {doctor.commission_percent != null && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg px-4 py-2">
                  <p className="text-xs text-gray-500">Commission</p>
                  <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{doctor.commission_percent}%</p>
                </div>
              )}
              {doctor.consultancy_type && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-4 py-2">
                  <p className="text-xs text-gray-500">Consultancy Type</p>
                  <p className="text-sm font-bold text-blue-600 dark:text-blue-400 capitalize">{doctor.consultancy_type}</p>
                </div>
              )}
            </div>

            {/* Additional Info Grid */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { label: 'Registration No.', value: doctor.registration_number },
                { label: 'Medical Council',  value: doctor.medical_council_name },
                { label: 'Clinic Name',      value: doctor.clinic_name },
                { label: 'Clinic Phone',     value: doctor.clinic_phone },
                { label: 'City',             value: doctor.city },
                { label: 'Address',          value: doctor.address },
              ].filter(i => i.value).map(({ label, value }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5">{value}</p>
                </div>
              ))}
            </div>

            {/* Bio */}
            {doctor.bio && (
              <div className="mt-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Bio</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{doctor.bio}</p>
              </div>
            )}

            {/* Documents */}
            {(doctor.registration_certificate_url || doctor.degree_certificate_url || doctor.id_proof_url) && (
              <div className="mt-3">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Documents</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Registration Certificate', url: doctor.registration_certificate_url },
                    { label: 'Degree Certificate',       url: doctor.degree_certificate_url },
                    { label: 'ID Proof (Aadhar/PAN)',    url: doctor.id_proof_url },
                  ].filter(d => d.url).map(({ label, url }) => (
                    <a key={label} href={url} target="_blank" rel="noreferrer"
                      className="group block border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-medihub-primary dark:hover:border-medihub-accent transition-colors">
                      <div className="h-32 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        {/\.(jpg|jpeg|png|webp|gif)$/i.test(url) ? (
                          <img src={url} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                            <FileText className="w-8 h-8 text-gray-400" />
                            <span className="text-xs text-gray-400">PDF Document</span>
                          </div>
                        )}
                      </div>
                      <div className="px-3 py-2 flex items-center justify-between">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{label}</p>
                        <FileText className="w-3.5 h-3.5 text-medihub-primary dark:text-medihub-highlight shrink-0 ml-1" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Appointment Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          {
            label: "Total",
            value: stats.total,
            color: "text-gray-900 dark:text-white",
          },
          {
            label: "Today",
            value: stats.today,
            color: "text-medihub-primary dark:text-medihub-highlight",
          },
          {
            label: "Upcoming",
            value: stats.upcoming,
            color: "text-green-600 dark:text-green-400",
          },
          {
            label: "Past",
            value: stats.past,
            color: "text-gray-600 dark:text-gray-400",
          },
          {
            label: "Cancelled",
            value: stats.cancelled,
            color: "text-red-600 dark:text-red-400",
          },
        ].map(({ label, value, color }) => (
          <div key={label} className="card p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* Appointment Tabs */}
      <div className="card overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-4">
          <div className="flex gap-1 overflow-x-auto pb-0">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab ? "border-medihub-primary text-medihub-primary dark:text-medihub-highlight" : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"}`}
              >
                {tab}
                <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">
                  {filterAppointments(appointments, tab).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-gray-500 dark:text-gray-400">
              No appointments found
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {[
                    "Patient",
                    "Date & Time",
                    "Type",
                    "Fee",
                    "Status",
                    "Notes",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((a) => (
                  <tr
                    key={a.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {a.patient_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-gray-700 dark:text-gray-300">
                        {formatDate(a.date)}
                      </p>
                      <p className="text-xs text-gray-500">{a.time}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {a.consultancy_type}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                      {formatCurrency(a.fee)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`badge ${getStatusColor(a.status)}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-[160px] truncate">
                      {a.reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Prescriptions */}
      {prescriptions?.length > 0 && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-medihub-primary" /> Patient
            Prescriptions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {prescriptions.map((p) => (
              <div
                key={p.id}
                className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-medihub-primary dark:hover:border-medihub-accent transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                      {p.patient}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(p.date)}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.medicines.map((m) => (
                        <span
                          key={m}
                          className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                    {p.notes && (
                      <p className="text-xs text-gray-500 mt-1.5 italic">
                        "{p.notes}"
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setPreviewFile(p)}
                    className="shrink-0 p-2 bg-medihub-light dark:bg-medihub-dark/30 text-medihub-primary dark:text-medihub-highlight rounded-lg hover:bg-medihub-soft transition-colors"
                  >
                    {p.fileType === "pdf" ? (
                      <FileText className="w-4 h-4" />
                    ) : (
                      <Image className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wallet Section */}
      <div className="space-y-4">
        {/* 5 Summary Cards */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          {[
            {
              label: "Total Earnings",
              value: formatCurrency(earnSummary?.total_earned ?? 0),
              icon: TrendingUp,
              color: "text-green-600 dark:text-green-400",
              bg: "bg-green-50 dark:bg-green-900/20",
            },
            {
              label: "Commission Deducted",
              value: formatCurrency(earnSummary?.total_commission ?? 0),
              icon: AlertCircle,
              color: "text-purple-600 dark:text-purple-400",
              bg: "bg-purple-50 dark:bg-purple-900/20",
            },
            {
              label: "Wallet Balance",
              value: formatCurrency(doctor?.wallet ?? 0),
              icon: Wallet,
              color: "text-medihub-primary dark:text-medihub-highlight",
              bg: "bg-medihub-light dark:bg-medihub-dark/20",
            },
            {
              label: "Total Withdrawn",
              value: formatCurrency(wdSummary?.total_withdrawn ?? 0),
              icon: ArrowDownCircle,
              color: "text-orange-600 dark:text-orange-400",
              bg: "bg-orange-50 dark:bg-orange-900/20",
            },
            {
              label: "Pending Requests",
              value: formatCurrency(wdSummary?.pending_amount ?? 0),
              icon: AlertCircle,
              color: "text-yellow-600 dark:text-yellow-400",
              bg: "bg-yellow-50 dark:bg-yellow-900/20",
            },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="card p-5 flex items-center gap-4">
              <div className={`p-3 rounded-xl shrink-0 ${bg}`}>
                <Icon className={`w-6 h-6 ${color}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {label}
                </p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Earnings History */}
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                Earnings History
              </h3>
            </div>
          </div>
          {!earnings?.length ? (
            <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No earnings yet
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {[
                      "Appointment ID",
                      "Patient",
                      "Doctor Fee",
                      "Commission",
                      "Doctor Earning",
                      "Date",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {earnings?.map((e) => (
                    <tr
                      key={e.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-medihub-primary dark:text-medihub-highlight whitespace-nowrap">
                        {e.id}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {e.patient_name}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatCurrency(e.consultation_fee ?? e.amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                          {e.commission_amount != null
                            ? formatCurrency(e.commission_amount)
                            : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-green-600 dark:text-green-400 whitespace-nowrap">
                        {formatCurrency(e.earned_amount ?? 0)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(e.appointment_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Withdraw Requests */}
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <div className="flex items-center gap-2">
              <ArrowDownCircle className="w-4 h-4 text-orange-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                Withdraw Requests
              </h3>
            </div>
            {/* <button
              onClick={() => {
                setWithdrawAmount("");
                setWithdrawModal(true);
              }}
              className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
            >
              <Plus className="w-3.5 h-3.5" />
              Request Withdraw
            </button> */}
          </div>
          {!withdrawals?.length ? (
            <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No withdraw requests
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {[
                      "Request ID",
                      "Amount",
                      "Status",
                      "Requested On",
                      "Processed On",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {withdrawals?.map((r) => (
                    <tr
                      key={r.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {r.id}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {formatCurrency(r.amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <WithdrawStatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(r.requested_at)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {r.resolved_at ? formatDate(r.resolved_at) : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.status === "pending" ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleApproveWithdraw(r.id)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectWithdraw(r.id)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Doctor Modal */}
      {doctor && (
        <Modal
          isOpen={editModal}
          onClose={() => setEditModal(false)}
          title="Edit Doctor"
          size="lg"
        >
          <EditDoctorForm
            doctor={doctor}
            onSubmit={handleEditSave}
            onClose={() => setEditModal(false)}
          />
        </Modal>
      )}

      {/* Request Withdraw Modal */}
      <Modal
        isOpen={withdrawModal}
        onClose={() => setWithdrawModal(false)}
        title="Request Withdraw"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-medihub-light dark:bg-medihub-dark/20 rounded-xl">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Available Balance
            </span>
            <span className="font-bold text-medihub-primary dark:text-medihub-highlight">
              {formatCurrency(doctor?.walletBalance ?? 0)}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Withdraw Amount ({getCurrencyConfig().symbol})
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={doctor?.walletBalance}
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="input-field"
              placeholder="Enter amount…"
              autoFocus
            />
            {parseFloat(withdrawAmount) > (doctor?.walletBalance ?? 0) && (
              <p className="text-xs text-red-500 mt-1">
                Amount exceeds wallet balance
              </p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={() => setWithdrawModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleRequestWithdraw}
              disabled={
                withdrawing ||
                !withdrawAmount ||
                parseFloat(withdrawAmount) <= 0 ||
                parseFloat(withdrawAmount) > (doctor?.walletBalance ?? 0)
              }
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {withdrawing ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </div>
      </Modal>

      {/* File Preview Modal */}
      <Modal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        title={`Preview: ${previewFile?.fileName}`}
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-8 flex flex-col items-center justify-center gap-3 min-h-[200px]">
            {previewFile?.fileType === "pdf" ? (
              <FileText className="w-16 h-16 text-red-400" />
            ) : (
              <Image className="w-16 h-16 text-medihub-highlight" />
            )}
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {previewFile?.fileName}
            </p>
            <p className="text-xs text-gray-500">
              File preview not available in demo mode
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1 text-sm">
            <p>
              <span className="text-gray-500">Patient:</span>{" "}
              <span className="font-medium">{previewFile?.patient}</span>
            </p>
            <p>
              <span className="text-gray-500">Date:</span>{" "}
              <span className="font-medium">
                {previewFile?.date && formatDate(previewFile.date)}
              </span>
            </p>
            <p>
              <span className="text-gray-500">Medicines:</span>{" "}
              <span className="font-medium">
                {previewFile?.medicines?.join(", ")}
              </span>
            </p>
            <p>
              <span className="text-gray-500">Notes:</span>{" "}
              <span className="font-medium">{previewFile?.notes}</span>
            </p>
          </div>
          <button
            onClick={() => setPreviewFile(null)}
            className="btn-secondary w-full"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default DoctorDetail;
